import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { PiggyBank, WifiOff } from 'lucide-react';
import { Button } from './ui/Button.tsx';

/** Sky logo tile shared by the centered AuthGate screens (DESIGN.md §3.2). */
function LogoTile() {
  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-sky-600 dark:bg-sky-500">
      <PiggyBank className="h-6 w-6 text-white" aria-hidden />
    </div>
  );
}

/** The authenticated user as returned by `GET /api/auth/me` (BUILD-SPEC §1). */
export interface CurrentUser {
  id: number;
  email: string;
  display_name: string | null;
  /** ADMIN_EMAIL match — gates the admin page + nav item. */
  is_admin?: boolean;
}

/**
 * Raised when `GET /api/auth/me` fails at the transport level (offline / backend
 * unreachable) — distinct from a real `401`. Phase 3, §5.6: a network error is
 * offline, NOT unauthenticated, so it must not redirect to login.
 */
export class OfflineError extends Error {
  constructor() {
    super('Keine Verbindung zum Server.');
    this.name = 'OfflineError';
  }
}

async function fetchCurrentUser(): Promise<CurrentUser> {
  let response: Response;
  try {
    response = await fetch('/api/auth/me', { credentials: 'include' });
  } catch {
    // fetch() rejects (TypeError) only on a transport failure — treat it as
    // offline, never as a 401.
    throw new OfflineError();
  }
  if (response.status === 401) {
    // Not logged in — hand off to the server-side OIDC login flow.
    window.location.href = '/api/auth/login';
    throw new Error('unauth');
  }
  if (!response.ok) {
    throw new Error(`Anmeldung konnte nicht geprüft werden (HTTP ${response.status})`);
  }
  const body = (await response.json()) as { data: CurrentUser };
  return body.data;
}

/** Full-screen offline fallback shown when the auth check never loaded and the
 * browser is offline (Phase 3, §5.6). Retry re-runs the query. */
function OfflineFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-white p-6 text-center dark:bg-slate-950">
      <WifiOff className="h-12 w-12 text-slate-300 dark:text-slate-600" aria-hidden />
      <p className="text-base font-semibold text-slate-900 dark:text-slate-100">Keine Verbindung</p>
      <p className="max-w-sm text-sm text-slate-400 dark:text-slate-500">
        Finance Tracker konnte den Server nicht erreichen. Prüfe deine Internetverbindung.
      </p>
      <div className="mt-3">
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Erneut versuchen
        </Button>
      </div>
    </div>
  );
}

interface AuthGateProps {
  children: (user: CurrentUser) => ReactNode;
}

/**
 * Login gate for the whole SPA. Queries the current user once; while pending it
 * shows a spinner, on 401 it redirects to `/api/auth/login`, and on success it
 * renders the authed shell with the resolved user. A network error (offline)
 * shows a retryable fallback instead of redirecting; if a user was already
 * cached this session, children keep rendering behind the offline banner.
 */
export function AuthGate({ children }: AuthGateProps) {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchCurrentUser,
    retry: false,
  });

  if (isPending) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white text-center dark:bg-slate-950"
      >
        <LogoTile />
        <div
          className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-sky-600 dark:border-slate-700 dark:border-t-sky-500"
          aria-hidden
        />
        <p className="text-sm text-slate-600 dark:text-slate-400">Anmeldung wird geprüft …</p>
      </div>
    );
  }

  // A cached user survives a failed refetch (offline): keep rendering the app
  // behind the OfflineBanner rather than blocking on the error.
  if (data) {
    return <>{children(data)}</>;
  }

  if (isError && error instanceof OfflineError) {
    return <OfflineFallback onRetry={() => void refetch()} />;
  }

  // A 401 has already triggered a redirect; this covers other failures
  // (backend down / malformed response) with a plain German message.
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-white p-6 text-center dark:bg-slate-950">
      <LogoTile />
      <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
        Anmeldung fehlgeschlagen.
      </p>
      <p className="max-w-sm text-sm text-slate-400 dark:text-slate-500">
        {error instanceof Error ? error.message : 'Bitte später erneut versuchen.'}
      </p>
    </div>
  );
}
